import {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useMemo,
} from "react";
import type { RowSelectionState } from "@tanstack/react-table";
import type { BoardPlacement } from "@design-system/Board/Board.types";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";

interface UseWorkBoardSelectionArgs {
    /** The page's engagements, whose row ids are the board's card ids. */
    cardRows: readonly ClientsWorkOverviewWorkRow[];
    /** List's checkbox selection, keyed by row id, which the board shares. */
    rowSelection: RowSelectionState;
    onRowSelectionChange: Dispatch<SetStateAction<RowSelectionState>>;
}

/**
 * The board's card selection, read from and written to List's row
 * selection, so the command island, the Actions palette and bulk delete
 * act on cards as they do on checked rows. Card ids are row ids, so every
 * copy of an application in people swimlanes selects together. Picks on
 * other pages stay held while the board's cards change.
 */
export const useWorkBoardSelection = ({
    cardRows,
    rowSelection,
    onRowSelectionChange,
}: UseWorkBoardSelectionArgs) => {
    const rowIds = useMemo(
        () => new Set(cardRows.map(({ id }) => id)),
        [cardRows],
    );

    const selectedItemIds = useMemo(
        (): ReadonlySet<string> =>
            new Set(
                Object.keys(rowSelection).filter(
                    (rowId) => rowSelection[rowId] && rowIds.has(rowId),
                ),
            ),
        [rowSelection, rowIds],
    );

    const onSelectionChange = useCallback(
        (itemIds: ReadonlySet<string>) =>
            onRowSelectionChange((previous) => ({
                ...Object.fromEntries(
                    Object.entries(previous).filter(
                        ([rowId]) => !rowIds.has(rowId),
                    ),
                ),
                ...Object.fromEntries(
                    [...itemIds]
                        .filter((rowId) => !isBusyClosesDemoId(rowId))
                        .map((rowId) => [rowId, true]),
                ),
            })),
        [onRowSelectionChange, rowIds],
    );

    /**
     * Drops selected cards the board doesn't show, so a bulk action never touches what the user can't see.
     * Unlike picks on another page, cards in a hidden column or lane are still loaded rows that the
     * selection would count and delete, so they're dropped instead of held.
     */
    const dropHiddenRows = useCallback(
        ({
            rows,
            getPlacements,
            isPlacementShown,
        }: {
            rows: readonly ClientsWorkOverviewWorkRow[];
            getPlacements: (
                row: ClientsWorkOverviewWorkRow,
            ) => BoardPlacement[];
            isPlacementShown: (placement: BoardPlacement) => boolean;
        }) =>
            onRowSelectionChange((previous) => {
                const hiddenRowIds = rows
                    .filter(
                        (row) =>
                            previous[row.id] &&
                            !getPlacements(row).some(isPlacementShown),
                    )
                    .map((row) => row.id);
                if (hiddenRowIds.length === 0) {
                    return previous;
                }
                const next = { ...previous };
                for (const rowId of hiddenRowIds) {
                    delete next[rowId];
                }
                return next;
            }),
        [onRowSelectionChange],
    );

    return { selectedItemIds, onSelectionChange, dropHiddenRows };
};
